import PageLayout from '@hellodarwin/app-core/lib/layouts/page-layout';
import SidebarPageLayout from '@hellodarwin/app-core/lib/layouts/sidebar-page-layout';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import AcceptedApplications from '@hellodarwin/app-features/lib/features/applications/accepted-applications';
import ApplicationFilesSidebarBlock from '@hellodarwin/app-features/lib/features/applications/application-files-sidebar-block';
import { ApplicationSidebarFile } from '@hellodarwin/app-features/lib/features/applications/application-files-sidebar-block/types';
import ApplicationProgression from '@hellodarwin/app-features/lib/features/applications/application-progression';
import ApplicationCandidateStatus from '@hellodarwin/app-features/lib/features/applications/candidate-status';
import type { MentionCandidate } from '@hellodarwin/app-features/lib/features/conversations/types';
import useSidebarManager from '@hellodarwin/app-features/lib/features/gin-steps/use-sidebar-manager';
import StepsManagerModal from '@hellodarwin/app-features/lib/features/steps-manager/modal';
import Button from '@hellodarwin/core/lib/components/common/button';
import Dropdown from '@hellodarwin/core/lib/components/common/dropdown';
import { ModalConfirm } from '@hellodarwin/core/lib/components/common/hd-modal/confirm';
import HdTag from '@hellodarwin/core/lib/components/common/hd-tag';
import getFileIcon from '@hellodarwin/core/lib/components/files/utils/get-file-icon';
import Loading from '@hellodarwin/core/lib/components/loading';
import {
  GinApplication,
  ProgramAdmin,
  ProgramGrantStatus,
  ProgramNeededProperties,
  ProgramUpdateProperties,
} from '@hellodarwin/core/lib/features/entities';
import {
  ProgramTargetUser,
  ProgramType,
} from '@hellodarwin/core/lib/features/entities/programs-entities';
import { getFullName } from '@hellodarwin/core/lib/features/helpers';
import useMediaQuery from '@hellodarwin/core/lib/features/helpers/use-media-query';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Dots from '@hellodarwin/icons/dist/icons/Dots';
import { ItemType } from 'antd/es/menu/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../../app/app-hooks';
import { downloadAsset } from '../../../../features/api/slices/assets-slice';
import { fetchApplicationNotifications } from '../../../../features/api/slices/client-application-notifications-slice';
import {
  fetchGinStepAssets,
  fetchGinStepForm,
  selectAllGinStepAssets,
  selectGinStepForm,
  selectIsGinFormReadyForProgram,
  selectIsGinStepAssetsLoading,
} from '../../../../features/api/slices/gin-steps-slice';
import {
  fetchAdmin,
  selectAdminByAdminId,
} from '../../../../features/api/slices/global-slice';
import {
  deleteClientProgram,
  fetchAcceptedPrograms,
  fetchGrantForProgram,
  selectGinApplicationById,
  selectGrantById,
  selectProgramById,
  selectRoadmapProgramById,
  updateProgramProperties,
} from '../../../../features/api/slices/programs-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../../../features/api/use-client-api';
import MainContent from './main-content';
import {
  CenteredApplicationContent,
  ClientSingleApplicationPageV2Actions,
  ClientSingleApplicationPageV2Content,
} from './styles';

const ClientSingleApplicationPage = () => {
  const { t } = useTranslation();
  const { t: t2 } = useTranslations();
  const api = useClientApi();
  const newApi = useNewClientApi();
  const dispatch = useAppDispatch();
  const { programId } = useParams();
  const { selectedLocale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { accessLevel } = usePremiumAccess();
  const ginApplication: GinApplication = useAppSelector((state) =>
    selectGinApplicationById(state, programId || ''),
  );
  const grantId = ginApplication?.application_program_grant_id || '';
  useSidebarManager();

  const canonicalProgram = useAppSelector((state) =>
    selectProgramById(state, programId || ''),
  );

  const roadmapProgram = useAppSelector((state) =>
    selectRoadmapProgramById(state, programId || ''),
  );

  // Fallback to roadmap cache if available (for navigation from programs list)
  const program = canonicalProgram || roadmapProgram;
  const hasCustomApplicationAccess =
    accessLevel === 'custom' ||
    ginApplication?.program_type === ProgramType.ALaCarte ||
    program?.program_type === ProgramType.ALaCarte;
  const isManagedProgram =
    ginApplication?.application_target_user === ProgramTargetUser.Admin;

  const programExpert = useAppSelector((state) =>
    program?.program_expert
      ? selectAdminByAdminId(state, program.program_expert || '')
      : null,
  );
  const programAccountManager = useAppSelector((state) =>
    program?.program_account_manager
      ? selectAdminByAdminId(
          state,
          program.program_account_manager || '',
        )
      : null,
  );
  const programAnalyst = useAppSelector((state) =>
    program?.program_analyst
      ? selectAdminByAdminId(state, program.program_analyst || '')
      : null,
  );

  const [
    applicationPropertiesModalConfig,
    setApplicationPropertiesModalConfig,
  ] = useState<{
    open: boolean;
    neededProperties?: ProgramNeededProperties;
  }>({
    open: false,
  });

  const handleEditPropertyInitiate = (
    property: keyof ProgramUpdateProperties,
    value: number,
  ) => {
    setApplicationPropertiesModalConfig({
      open: true,
      neededProperties: {
        draft_first_version: {
          amount: property === 'program_targeted_amount' ? value : null,
        },
        submit_application: {
          time: null,
          amount: property === 'program_application_amount' ? value : null,
        },
        decisions_made: {
          time: null,
          amount: property === 'program_accepted_amount' ? value : null,
          refusal_reason: null,
        },
      },
    });
  };

  useEffect(() => {
    if (!programId) return;
    dispatch(fetchApplicationNotifications({ api: newApi }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => {
    if (
      !programId ||
      !ginApplication?.application_id ||
      isManagedProgram
    )
      return;
    dispatch(
      fetchGinStepForm({
        api: newApi,
        programId,
        ownerType: hasCustomApplicationAccess ? 'Client' : '',
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    programId,
    ginApplication?.application_id,
    isManagedProgram,
    hasCustomApplicationAccess,
  ]);

  useEffect(() => {
    if (!isManagedProgram || !/^#step-\d+-\d+$/.test(location.hash)) return;
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true },
    );
  }, [
    isManagedProgram,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  const goBack = () => {
    navigate(-1);
  };
  const isSmallDesktop = useMediaQuery({ maxWidth: theme.breakpoints.md });

  const mentionCandidates: MentionCandidate[] = useMemo(() => {
    const out = new Map<string, MentionCandidate>();
    if (programAccountManager?.admin_id && programAccountManager?.first_name) {
      out.set(programAccountManager.admin_id, {
        targetType: 'admin',
        targetId: programAccountManager.admin_id,
        label: getFullName(
          programAccountManager.first_name,
          programAccountManager.last_name,
        ),
      });
    }
    if (programAnalyst?.admin_id && programAnalyst?.first_name) {
      out.set(programAnalyst.admin_id, {
        targetType: 'admin',
        targetId: programAnalyst.admin_id,
        label: getFullName(programAnalyst.first_name, programAnalyst.last_name),
      });
    }
    if (programExpert?.admin_id && programExpert?.first_name) {
      out.set(programExpert.admin_id, {
        targetType: 'admin',
        targetId: programExpert.admin_id,
        label: getFullName(programExpert.first_name, programExpert.last_name),
      });
    }
    return Array.from(out.values());
  }, [programAccountManager, programAnalyst, programExpert]);

  const programAdmins = useMemo(
    () =>
      [
        programAccountManager
          ? ({
              ...programAccountManager,
              role: 'AccountManager',
            } as ProgramAdmin)
          : undefined,
        programAnalyst
          ? ({ ...programAnalyst, role: 'Analyst' } as ProgramAdmin)
          : undefined,
        programExpert
          ? ({ ...programExpert, role: 'Expert' } as ProgramAdmin)
          : undefined,
      ].filter((admin): admin is ProgramAdmin => Boolean(admin?.admin_id)),
    [programAccountManager, programAnalyst, programExpert],
  );

  useEffect(() => {
    if (!ginApplication?.application_id) return;

    if (!isManagedProgram) {
      dispatch(
        fetchGinStepAssets({
          api: newApi,
          programId: ginApplication.application_id,
        }),
      );
    }

    if (!ginApplication.application_program_grant_id) return;

    dispatch(
      fetchGrantForProgram({
        api,
        grantId: ginApplication.application_program_grant_id,
        locale: selectedLocale,
        programId: ginApplication.application_id,
      }),
    );
    if (!isManagedProgram) {
      dispatch(
        fetchAcceptedPrograms({
          api,
          grantId: ginApplication.application_program_grant_id,
          programIdToExclude: ginApplication.application_id,
          locale: selectedLocale,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ginApplication?.application_id,
    ginApplication?.application_program_grant_id,
    isManagedProgram,
    selectedLocale,
  ]);

  useEffect(() => {
    if (!!program?.program_expert) {
      dispatch(
        fetchAdmin({ api, adminId: program.program_expert || '' }),
      );
    }
    if (!!program?.program_account_manager) {
      dispatch(
        fetchAdmin({
          api,
          adminId: program.program_account_manager || '',
        }),
      );
    }
    if (!!program?.program_analyst) {
      dispatch(
        fetchAdmin({ api, adminId: program.program_analyst || '' }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program]);

  const grant = useAppSelector((state) =>
    selectGrantById(state, ginApplication?.application_program_grant_id || ''),
  );

  const programDisplayName =
    ginApplication?.application_name ||
    program?.program_name ||
    grant?.grant_display_title ||
    ginApplication?.application_company_name ||
    '';

  const ginStepForm = useAppSelector((state) => selectGinStepForm(state));
  const isGinFormReady = useAppSelector((state) =>
    selectIsGinFormReadyForProgram(state, programId || ''),
  );
  const ginApplicationAssets = useAppSelector((state) =>
    selectAllGinStepAssets(state),
  );
  const isGinApplicationAssetsLoading = useAppSelector(
    selectIsGinStepAssetsLoading,
  );

  const handleDownloadAsset = useCallback(
    async (assetId: string) => {
      try {
        const url = (
          await dispatch(
            downloadAsset({ api: newApi, asset_id: assetId }),
          ).unwrap()
        ).link;

        window.open(url, 'download');
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch, newApi],
  );

  const applicationFiles: ApplicationSidebarFile[] = useMemo(() => {
    return (ginApplicationAssets || []).map((asset) => ({
      assetId: asset.asset_id,
      displayName: asset.display_name,
      extension: asset.extension,
      createdAt: asset.created_at,
      fileSize: asset.file_size,
      Icon: getFileIcon(asset),
    }));
  }, [ginApplicationAssets]);

  const templateFiles: ApplicationSidebarFile[] = useMemo(() => {
    return (ginStepForm?.example_assets_preview || []).reduce((acc, asset) => {
      if (!asset.file_response) return acc;
      const fileResponse = asset.file_response;
      acc.push({
        assetId: fileResponse.asset_id,
        displayName:
          selectedLocale === 'en' ? asset.client_name_en : asset.client_name_fr,
        extension: fileResponse.extension,
        createdAt: fileResponse.created_at,
        fileSize: fileResponse.file_size,
        Icon: getFileIcon(fileResponse),
      });
      return acc;
    }, [] as ApplicationSidebarFile[]);
  }, [ginStepForm, selectedLocale]);

  const rightSidebarBlocks = useMemo(() => {
    const blocks = [];

    if (
      !!ginStepForm &&
      ginStepForm.properties_preview &&
      ginStepForm.properties_preview.length > 0 &&
      !!ginApplication &&
      !!ginApplication.application_program_grant_id &&
      !isManagedProgram &&
      !hasCustomApplicationAccess
    ) {
      blocks.push({
        id: 'favoured_candidates',
        content: (
          <ApplicationCandidateStatus
            properties={ginStepForm.properties_preview}
            programName={programDisplayName}
            programId={ginApplication.application_program_grant_id || ''}
          />
        ),
        fixed: true,
      });
    }

    if (
      !!ginApplication?.application_program_grant_id &&
      !isManagedProgram &&
      !hasCustomApplicationAccess
    ) {
      blocks.push({
        id: 'accepted_applications',
        title: t2('gin_steps|acceptedApplications'),
        content: (
          <AcceptedApplications
            grant_id={ginApplication?.application_program_grant_id || ''}
          />
        ),
      });
    }

    return blocks;
  }, [
    ginStepForm,
    ginApplication,
    t2,
    hasCustomApplicationAccess,
    isManagedProgram,
    programDisplayName,
  ]);

  const flexibleLeftSidebarBlocks = useMemo(() => {
    return [
      {
        id: 'application_files',
        title: t2('application_single|myApplicationFiles'),
        content: isGinApplicationAssetsLoading ? (
          <Loading />
        ) : (
          <ApplicationFilesSidebarBlock
            files={applicationFiles}
            emptyText={t2('application_single|addFilesToApplication')}
            handleDownload={handleDownloadAsset}
          />
        ),
      },
      {
        id: 'helloDarwin_templates',
        title: t2('application_single|helloDarwinTemplates'),
        content: (
          <ApplicationFilesSidebarBlock
            files={templateFiles}
            emptyText={t2('application_single|noTemplates')}
            handleDownload={handleDownloadAsset}
          />
        ),
      },
    ];
  }, [
    applicationFiles,
    templateFiles,
    t2,
    isGinApplicationAssetsLoading,
    handleDownloadAsset,
  ]);

  const items: ItemType[] = useMemo(() => {
    const menu: ItemType[] = [];

    menu.push({
      label: t('application|delete_application'),
      key: 'delete',
      onClick: () => {
        ModalConfirm({
          title: t('application|delete_application'),
          content: t('application|delete_application_message'),
          okText: t('application|delete'),
          cancelText: t('application|cancel'),
          okButtonProps: {
            style: {
              backgroundColor: theme.colors.purple_1,
              color: theme.colors.white_1,
              borderRadius: '24px',
            },
          },
          cancelButtonProps: {
            style: {
              backgroundColor: theme.colors.white_1,
              borderColor: theme.colors.grey_1,
              color: theme.colors.grey_1,
              borderRadius: '24px',
            },
          },
          onOk: async () => {
            if (!programId) return;
            dispatch(
              deleteClientProgram({
                api: newApi,
                programId,
              }),
            );
            navigate(`/programs/roadmap`);
          },
        });
      },
    });

    return menu;
  }, [t, theme.colors, programId, dispatch, navigate, newApi]);

  const applicationActions =
    ginApplication?.application_program_client_requested_at ||
    !hasCustomApplicationAccess ? (
      <Dropdown
        items={items}
        trigger={['click']}
        placement="bottomRight"
        ctaRender={() => (
          <Button size="square" style={{ padding: 0 }} transparent>
            <Dots size={16} />
          </Button>
        )}
      />
    ) : undefined;

  if (!ginApplication || (!isManagedProgram && !isGinFormReady)) {
    return <Loading />;
  }

  if (!isSmallDesktop) {
    return (
      <SidebarPageLayout
        handleBack={goBack}
        app="client"
        title={programDisplayName}
        actions={isManagedProgram ? applicationActions : undefined}
        breadcrumbs={[
          {
            breadcrumbName: t('navigation.home'),
            path: '/',
          },
          {
            breadcrumbName: t('navigation.ongoingPrograms'),
            path: '/applications',
          },
          {
            breadcrumbName: programDisplayName,
          },
        ]}
        leftSidebarBlocks={
          isManagedProgram
            ? []
            : [
                {
                  id: 'dropdown',
                  content: (
                    <ClientSingleApplicationPageV2Actions>
                      {applicationActions}
                    </ClientSingleApplicationPageV2Actions>
                  ),
                  fixed: true,
                },
                ...(!!ginApplication.application_program_client_requested_at &&
                ginApplication.application_status ===
                  ProgramGrantStatus.Identified
                  ? [
                      {
                        id: 'sent_to_hellodarwin',
                        content: (
                          <ClientSingleApplicationPageV2Content>
                            <HdTag
                              text={t('programs_labels|sent_to_hd_tag')}
                              size="small"
                              color={theme.colors.purple_4}
                              withBorder
                              highContrastText
                            />
                          </ClientSingleApplicationPageV2Content>
                        ),
                        fixed: true,
                      },
                    ]
                  : []),
                {
                  id: 'application_progression',
                  content: (
                    <>
                      <ApplicationProgression
                        application={ginApplication}
                        ginStepForm={ginStepForm!}
                        grantName={programDisplayName}
                        handleEditPropertyInitiate={handleEditPropertyInitiate}
                      />
                      {(applicationPropertiesModalConfig.neededProperties as ProgramNeededProperties) && (
                        <StepsManagerModal
                          open={applicationPropertiesModalConfig.open}
                          onClose={() =>
                            setApplicationPropertiesModalConfig({ open: false })
                          }
                          onConfirm={(properties) => {
                            if (!programId) return;
                            dispatch(
                              updateProgramProperties({
                                api: newApi,
                                programId,
                                properties,
                              }),
                            );
                            setApplicationPropertiesModalConfig({ open: false });
                          }}
                          neededProperties={
                            applicationPropertiesModalConfig.neededProperties as ProgramNeededProperties
                          }
                        />
                      )}
                    </>
                  ),
                  fixed: true,
                },
                ...flexibleLeftSidebarBlocks,
              ]
        }
        {...(rightSidebarBlocks.length > 0 && {
          rightSidebarBlocks,
        })}
        noFooter
      >
        {programId && (
          <CenteredApplicationContent>
            <MainContent
              programId={programId}
              grantId={grantId}
              displayTitle={programDisplayName}
              program={program}
              programAdmins={
                hasCustomApplicationAccess || isManagedProgram
                  ? programAdmins
                  : []
              }
              mentionCandidates={mentionCandidates}
              hasCustomApplicationAccess={hasCustomApplicationAccess}
              isManagedProgram={isManagedProgram}
            />
          </CenteredApplicationContent>
        )}
      </SidebarPageLayout>
    );
  } else {
    return (
      <PageLayout
        handleBack={goBack}
        app="client"
        title={programDisplayName}
        breadcrumbs={[
          {
            breadcrumbName: t('navigation.home'),
            path: '/',
          },
          {
            breadcrumbName: t('navigation.programs'),
            path: '/programs',
          },
          {
            breadcrumbName: t('navigation.ongoingPrograms'),
            path: '/applications',
          },
          {
            breadcrumbName: programDisplayName,
          },
        ]}
      >
        {programId && (
          <CenteredApplicationContent>
            <MainContent
              programId={programId}
              grantId={grantId}
              displayTitle={programDisplayName}
              program={program}
              programAdmins={
                hasCustomApplicationAccess || isManagedProgram
                  ? programAdmins
                  : []
              }
              mentionCandidates={mentionCandidates}
              hasCustomApplicationAccess={hasCustomApplicationAccess}
              isManagedProgram={isManagedProgram}
            />
          </CenteredApplicationContent>
        )}
      </PageLayout>
    );
  }
};

export default ClientSingleApplicationPage;
